import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { BsFillPauseCircleFill, BsPlayCircleFill } from 'react-icons/bs';
import { FiArrowLeftCircle } from 'react-icons/fi';

import { LoadingOutlined } from '@ant-design/icons';
import BookImage from '@assets/images/ai4.png';
import audioPath from '@assets/sounds/flipcard.mp3';
// import type { IGetStoryVoice, IGetWhatIfStoryVoice } from '@coreStory/type';
import {
  useGetStoryVoice,
  useGetWhatIfStoryVoice,
  useSaveStory,
  useUnSaveStory,
} from '@coreStory/useCase';
import { Spin, Menu } from 'antd';
import cs from 'classnames';
import { BiSkipNext, BiSkipPrevious } from 'react-icons/bi';
import HTMLFlipBook from 'react-pageflip';
import styles from './index.module.scss';
import { useParams } from 'react-router-dom';
import { WhatIfStoryViewResponse } from 'src/core/repo/whatif/types';

interface IWebViewProps {
  storyData: WhatIfStoryViewResponse[];
  onStoryUpdated?: () => void;
  onSetVisibility?: () => void;
}

const WebView: FC<IWebViewProps> = ({
  storyData,
  onStoryUpdated,
  onSetVisibility,
}) => {
  const { id } = useParams();
  const nullRef = useRef<HTMLAudioElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  let voiceRef = useRef<HTMLAudioElement>(null);
  const book = useRef<any>(null);
  const [data, setData] = useState<WhatIfStoryViewResponse[]>([]);
  const [audioStorySrc, setAudioStorySrc] = useState<string>();
  const [isVoicePlaying, setIsVoicePlaying] = useState<boolean>(false);
  const [isVoiceCalled, setIsVoiceCalled] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isEditTitleModalVisible, setIsEditTitleModalVisible] = useState(false);
  const [currentEditStory, setCurrentEditStory] =
    useState<WhatIfStoryViewResponse | null>(null);
  const [currentEditTitleStory, setCurrentEditTitleStory] =
    useState<WhatIfStoryViewResponse | null>(null);
  const { mutate: saveStory } = useSaveStory();
  const { mutate: unSaveStory } = useUnSaveStory();

  const {
    mutate,
    data: voiceData,
    isSuccess: voiceSuccess,
    isLoading: voiceLoading,
  } = useGetWhatIfStoryVoice();

  const handleSaveStory = () => {
    id &&
      saveStory(id, {
        onSuccess() {
          onStoryUpdated?.();
        },
      });
  };

  const handleUnSaveStory = () => {
    id &&
      unSaveStory(id, {
        onSuccess() {
          onStoryUpdated?.();
        },
      });
  };

  const handlePlay = useCallback(() => {
    audioRef.current && audioRef.current?.play();
    voiceRef?.current && voiceRef.current.pause();
    setIsPlaying(false);
    voiceRef = nullRef;
    setAudioStorySrc('');
    setIsVoiceCalled(false);
    setIsVoicePlaying(false);
  }, []);

  const handleGetAudio = (item: WhatIfStoryViewResponse) => {
    setIsVoiceCalled(true);
    const data = {
      story_content_id: item.StoryID,
      story_id: item.StoryContentID,
      contentVOrder: item.contentVOrder,
    };
    data && mutate(data);
  };

  const handleEditStory = (story: WhatIfStoryViewResponse) => {
    setCurrentEditStory(story);
    setIsEditModalVisible(true);
  };

  const handleEditStoryTitle = (story: WhatIfStoryViewResponse) => {
    setCurrentEditTitleStory(story);
    setIsEditTitleModalVisible(true);
  };

  const handleEditSuccess = () => {
    // Refresh the story data after successful edit
    setData([...data]);
    onStoryUpdated?.();
  };

  const handleEditTitleSuccess = () => {
    // Refresh the story data after successful title edit
    setData([...data]);
    onStoryUpdated?.();
  };

  const handleCancelEdit = () => {
    setIsEditModalVisible(false);
    setCurrentEditStory(null);
  };

  const handleCancelEditTitle = () => {
    setIsEditTitleModalVisible(false);
    setCurrentEditTitleStory(null);
  };

  useEffect(() => {
    if (storyData.length) {
      setData(storyData);
    }
  }, [storyData]);

  const playingButton = () => {
    if (voiceRef.current)
      if (isPlaying) {
        voiceRef.current.pause();
        setIsPlaying(false);
      } else {
        voiceRef.current.play();
        setIsPlaying(true);
      }
  };

  useEffect(() => {
    if (voiceData && voiceSuccess) {
      if (voiceRef.current) voiceRef.current.play();
      setAudioStorySrc(voiceData);
    }
  }, [voiceSuccess, voiceData]);

  const width = window.innerWidth;
  return (
    <div className={styles['web_book']}>
      <audio
        style={{ display: 'none' }}
        ref={audioRef}
        src={audioPath}
        controls
      />
      <audio
        ref={voiceRef}
        src={`http://${voiceData}`}
        style={{ visibility: 'hidden', position: 'absolute' }}
        onEnded={() => setIsPlaying(false)}
        onPlaying={() => {
          setIsPlaying(true);
          setIsVoicePlaying(true);
        }}
        controls
      />
      <HTMLFlipBook
        onChangeState={handlePlay}
        ref={book}
        className={''}
        style={{ width: '450px' }}
        startPage={1}
        size={'stretch'}
        width={width > 1000 ? 450 : 300}
        height={width > 1000 ? 450 : 300}
        minWidth={450}
        maxWidth={100}
        minHeight={450}
        maxHeight={450}
        drawShadow={true}
        flippingTime={500}
        usePortrait={false}
        startZIndex={20}
        autoSize={true}
        maxShadowOpacity={1}
        showCover={false}
        mobileScrollSupport={true}
        clickEventForward={true}
        useMouseEvents={false}
        swipeDistance={500}
        showPageCorners={false}
        disableFlipByClick={false}>
        {data.map((item, index) => {
          const menu = (
            <Menu>
              <Menu.Item
                key="editTitle"
                onClick={() => handleEditStoryTitle(item)}>
                Edit Title
              </Menu.Item>
              <Menu.Item key="edit" onClick={() => handleEditStory(item)}>
                Edit Content
              </Menu.Item>
              <Menu.Item key="visibility" onClick={onSetVisibility}>
                {'visibility'}
              </Menu.Item>
            </Menu>
          );

          return index % 2 === 0 ? (
            <div className="demoPage">
              <div className={styles['image_book']}>
                {item.StoryImage ? (
                  <img src={`http://${item.StoryImage}`} alt="Story Image" />
                ) : (
                  <img src={BookImage} alt="Default Image" />
                )}
                {index === 0 ? null : (
                  <div
                    className={styles['change_page_img']}
                    onClick={() => {
                      book.current.pageFlip().flipPrev();
                    }}>
                    <FiArrowLeftCircle
                      size={34}
                      className={styles['arrow-prev-web']}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="demoPage">
              <div className={styles['page_book']}>
                <div className={styles['text_book']}>
                  <p>{item?.StoryText}</p>
                </div>
                <div className={styles['sound_player']}>
                  <div className={styles['sound_player_buttons']}>
                    <div
                      className={cs([
                        styles['icons_left'],
                        index === 1 ? styles['disabled'] : '',
                      ])}
                      onClick={() => {
                        book.current.pageFlip().flipPrev();
                      }}>
                      <BiSkipPrevious size={55} />
                    </div>
                    {!audioStorySrc && !isVoiceCalled ? (
                      <div
                        className={styles['icons']}
                        onClick={() => handleGetAudio(item)}>
                        <BsPlayCircleFill size={60} />
                      </div>
                    ) : voiceLoading || !isVoicePlaying ? (
                      <Spin
                        indicator={
                          <LoadingOutlined
                            className={styles['audio_icon_play']}
                            style={{ fontSize: 42 }}
                            spin
                          />
                        }
                      />
                    ) : isPlaying ? (
                      <div className={styles['icons']} onClick={playingButton}>
                        <BsFillPauseCircleFill size={60} />
                      </div>
                    ) : (
                      <div className={styles['icons']} onClick={playingButton}>
                        <BsPlayCircleFill size={60} />
                      </div>
                    )}
                    <div
                      onClick={() => {
                        book.current.pageFlip().flipNext();
                      }}
                      className={cs([
                        styles['icons_right'],
                        index + 1 === data.length ? styles['disabled'] : '',
                      ])}>
                      <BiSkipNext size={55} />
                    </div>
                  </div>
                  <span className={styles['number_of_page']}>
                    {item?.VOrder} / {data.length / 2}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </HTMLFlipBook>
    </div>
  );
};

export default WebView;
